import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { Firma } from '../types';

export const useFirmaOptions = () => {
  const { user, profile, userFirmas } = useAuth();
  const [firmaOptions, setFirmaOptions] = useState<Firma[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userFirmas && userFirmas.length > 0) {
      const firmalar = userFirmas.map(uf => ({
        id: uf.firma_id,
        value: uf.firma_adi,
        label: uf.firma_adi,
        firma_adi: uf.firma_adi,
        logo_url: ''
      }));
      setFirmaOptions(firmalar);
      setIsLoading(false);
    }
  }, [userFirmas]);

  const loadFirmaOptions = useCallback(async () => {
    if (!user || !profile) {
      if (firmaOptions.length > 0) {
        setFirmaOptions([]);
      }
      return;
    }

    if (userFirmas && userFirmas.length > 0) {
      const firmalar = userFirmas.map(uf => ({
        id: uf.firma_id,
        value: uf.firma_adi,
        label: uf.firma_adi,
        firma_adi: uf.firma_adi,
        logo_url: ''
      }));
      setFirmaOptions(firmalar);
      return;
    }

    if (isLoading) return;

    if (
      firmaOptions.length > 0 &&
      profile.rol !== 'firma_super_admin' &&
      profile.rol !== 'ekostrateji_super_admin'
    ) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let firmalar: Firma[] = [];

      if (profile.rol === 'ekostrateji_super_admin') {
        let { data, error } = await supabase.rpc('get_all_firmalar_for_super_admin');

        if (error || !data) {
          const result = await supabase
            .from('firmalar')
            .select('id, firma_adi, logo_url')
            .eq('is_active', true)
            .order('firma_adi');
          data = result.data;
          error = result.error;
        }

        if (!error && data && data.length > 0) {
          firmalar = data.map((f: any) => ({
            id: f.id,
            value: f.firma_adi,
            label: f.firma_adi,
            firma_adi: f.firma_adi,
            logo_url: f.logo_url
          }));
        }
      } else if (profile.rol === 'firma_super_admin') {
        const { data: relatedFirmas, error: userError } = await supabase
          .from('user_firma_relations')
          .select(`
            firma_id,
            firmalar(id, firma_adi, logo_url)
          `)
          .eq('user_id', user.id)
          .eq('is_active', true);

        if (!userError && relatedFirmas && relatedFirmas.length > 0) {
          const firmaIds = relatedFirmas.map((uf: any) => uf.firma_id);

          const { data: firmaData, error: firmaError } = await supabase
            .rpc('get_all_firmalar_for_super_admin');

          if (!firmaError && firmaData) {
            firmalar = firmaData
              .filter((f: any) => firmaIds.includes(f.id))
              .map((f: any) => ({
                id: f.id,
                value: f.firma_adi,
                label: f.firma_adi,
                firma_adi: f.firma_adi,
                logo_url: f.logo_url
              }));
          }
        }
      } else {
        const { data: relatedFirmas, error: userError } = await supabase
          .from('user_firma_relations')
          .select(`
            firma_id,
            firmalar!inner(id, firma_adi, logo_url)
          `)
          .eq('user_id', user.id)
          .eq('is_active', true);

        if (!userError && relatedFirmas) {
          firmalar = relatedFirmas.map((uf: any) => ({
            id: uf.firmalar.id,
            value: uf.firmalar.firma_adi,
            label: uf.firmalar.firma_adi,
            firma_adi: uf.firmalar.firma_adi,
            logo_url: uf.firmalar.logo_url
          }));
        }
      }

      if (firmalar.length > 0) {
        setFirmaOptions(firmalar);
      }
    } catch (err: any) {
      setError(err.message);
      setFirmaOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, profile?.rol, profile?.firma_id, userFirmas, firmaOptions.length, isLoading]);

  useEffect(() => {
    if (user && profile) {
      loadFirmaOptions();
    }
  }, [user?.id, profile?.rol, loadFirmaOptions]);

  return {
    firmaOptions,
    isLoading,
    error,
    refetch: loadFirmaOptions
  };
};
